import { useState, useEffect } from "react";
import { Search, Eye, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_COLORS = { placed: "bg-blue-100 text-blue-700", confirmed: "bg-cyan-100 text-cyan-700", packed: "bg-indigo-100 text-indigo-700", shipped: "bg-amber-100 text-amber-700", delivered: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700" };
const ORDER_STATUSES = ["placed", "confirmed", "packed", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [trackingNum, setTrackingNum] = useState("");

  const fetchOrders = () => {
    setLoading(true);
    axios.get(`${API}/admin/orders?page=${page}&status=${filter}`, { withCredentials: true })
      .then(r => { setOrders(r.data.orders || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOrders(); }, [page, filter]);

  const updateStatus = async (orderId) => {
    try {
      const body = { status: newStatus };
      if (trackingNum) body.tracking_number = trackingNum;
      await axios.put(`${API}/admin/orders/${orderId}/status`, body, { withCredentials: true });
      toast.success(`Order ${newStatus}`);
      fetchOrders();
      setShowDetail(false);
    } catch (err) { toast.error("Failed to update status"); }
  };

  const viewOrder = (order) => { setSelected(order); setNewStatus(order.status); setTrackingNum(order.tracking_number || ""); setShowDetail(true); };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Orders ({total})</h1>
        <Select value={filter} onValueChange={v => { setFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-40 rounded-xl" data-testid="order-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {ORDER_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead><tr className="text-left text-xs text-slate-500 border-b bg-slate-50"><th className="p-4 font-medium">Order ID</th><th className="p-4 font-medium">Customer</th><th className="p-4 font-medium">Items</th><th className="p-4 font-medium">Total</th><th className="p-4 font-medium">Payment</th><th className="p-4 font-medium">Status</th><th className="p-4 font-medium">Date</th><th className="p-4 font-medium">Action</th></tr></thead>
            <tbody>
              {loading ? [...Array(5)].map((_, i) => <tr key={i}><td colSpan={8} className="p-4"><div className="h-10 skeleton-loading rounded" /></td></tr>) :
              orders.map(o => (
                <tr key={o.order_id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="p-4 font-medium text-slate-800">{o.order_id}</td>
                  <td className="p-4"><p className="text-slate-700">{o.user_name}</p><p className="text-xs text-slate-400">{o.user_email}</p></td>
                  <td className="p-4 text-slate-600">{o.items?.length || 0} items</td>
                  <td className="p-4 font-semibold">Rs.{o.total}</td>
                  <td className="p-4 text-slate-600 uppercase text-xs">{o.payment_method}</td>
                  <td className="p-4"><Badge className={`text-xs capitalize ${STATUS_COLORS[o.status] || 'bg-slate-100'}`}>{o.status}</Badge></td>
                  <td className="p-4 text-slate-500 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="p-4"><button onClick={() => viewOrder(o)} className="p-1.5 hover:bg-cyan-50 rounded-lg text-slate-500 hover:text-cyan-600" data-testid={`view-order-${o.order_id}`}><Eye className="w-4 h-4" /></button></td>
                </tr>
              ))}
              {!loading && orders.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No orders found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto rounded-2xl">
          <DialogHeader><DialogTitle>Order: {selected?.order_id}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="bg-sky-50 rounded-xl p-4 text-sm space-y-1">
                <p><strong>Customer:</strong> {selected.user_name} ({selected.user_email})</p>
                <p><strong>Payment:</strong> {selected.payment_method?.toUpperCase()} - {selected.payment_status}</p>
                <p><strong>Address:</strong> {selected.delivery_address?.address_line1}, {selected.delivery_address?.city}</p>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-2">Items</h4>
                <div className="space-y-2">
                  {selected.items?.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <img src={item.image} alt="" className="w-8 h-8 rounded-lg object-cover bg-slate-100" />
                      <span className="flex-1">{item.name} x{item.quantity}</span>
                      <span className="font-medium">Rs.{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t mt-3 pt-3 text-sm font-bold flex justify-between"><span>Total</span><span>Rs.{selected.total}</span></div>
              </div>
              <div className="border-t pt-4">
                <Label className="text-xs font-semibold">Update Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="update-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ORDER_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
                {(newStatus === "shipped" || newStatus === "delivered") && (
                  <div className="mt-3"><Label className="text-xs">Tracking Number</Label><Input value={trackingNum} onChange={e => setTrackingNum(e.target.value)} className="rounded-xl mt-1" placeholder="Enter tracking number" data-testid="tracking-input" /></div>
                )}
                <Button onClick={() => updateStatus(selected.order_id)} className="mt-3 rounded-full bg-cyan-600 hover:bg-cyan-700 w-full" data-testid="update-order-btn"><Truck className="w-4 h-4 mr-1" /> Update Status</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
